import React from 'react';
import type { PaginationState } from './PaginatedTable';

export interface PaginatedListProps<T> {
  data: T[];
  pagination: PaginationState | null;
  loading?: boolean;
  emptyMessage?: string;
  onPageChange: (page: number) => void;
  renderItem: (item: T, index: number) => React.ReactNode;
  getItemKey?: (item: T, index: number) => string | number;
  /** Optional: show "X–Y of Z results" summary */
  showResultsSummary?: boolean;
  /** Optional: override the default vertical list container (e.g. grid layout) */
  listClassName?: string;
}

export function PaginatedList<T>({
  data,
  pagination,
  loading = false,
  emptyMessage = 'No items found.',
  onPageChange,
  renderItem,
  getItemKey = (item, index) => (item as { id?: string }).id ?? index,
  showResultsSummary = true,
  listClassName,
}: PaginatedListProps<T>) {
  const from = pagination ? (pagination.page - 1) * pagination.limit + 1 : 1;
  const to = pagination ? Math.min(pagination.page * pagination.limit, pagination.total) : data.length;

  return (
    <div className="space-y-3">
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-600 dark:border-gray-400" />
        </div>
      ) : (
        <>
          {showResultsSummary && pagination && pagination.total > 0 && (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Showing {from}–{to} of {pagination.total} results
            </p>
          )}
          {data.length === 0 ? (
            <p className="py-8 text-center text-gray-500 dark:text-gray-400">{emptyMessage}</p>
          ) : (
            <div className={listClassName ?? 'space-y-3'}>
              {data.map((item, index) => (
                <React.Fragment key={getItemKey(item, index)}>
                  {renderItem(item, index)}
                </React.Fragment>
              ))}
            </div>
          )}
          {pagination && pagination.totalPages > 1 && (
            <div className="mt-4 flex items-center justify-between border-t border-gray-200 dark:border-gray-700 pt-4">
              <span className="text-sm text-gray-500 dark:text-gray-400">
                Page {pagination.page} of {pagination.totalPages}
              </span>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => onPageChange(Math.max(1, pagination.page - 1))}
                  disabled={pagination.page <= 1}
                  className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded disabled:opacity-50 hover:bg-gray-50 dark:hover:bg-gray-800"
                >
                  Previous
                </button>
                <button
                  type="button"
                  onClick={() => onPageChange(pagination.page + 1)}
                  disabled={pagination.page >= pagination.totalPages}
                  className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded disabled:opacity-50 hover:bg-gray-50 dark:hover:bg-gray-800"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
